/**
 * HWP 5.0 — read-only text extraction for the Korean binary format (§8, §18).
 *
 * HWP 5.0 is a Compound File (OLE2) whose `BodyText/SectionN` streams hold the
 * document as records; the text lives in `PARA_TEXT` records, raw-DEFLATE
 * compressed when the FileHeader says so. This reads those out into paragraphs
 * shaped like the document model everything else uses, so an HWP opens in the
 * same viewer as a Word file.
 *
 * **This is deliberately labelled unvalidated.** The catalog rates HWP 5.0
 * `needs-validation`, and that is honest: the parser is written to the public
 * specification and verified against a spec-correct fixture, but it has not
 * been run against the messy variety of real 한글 files, and HWP has a long
 * history as a malware carrier (§30). So it opens read-only, it never writes
 * HWP back, and it carries a warning that says plainly it is text extraction
 * awaiting real-file validation — §19 forbids presenting "we wrote a parser" as
 * "we support this format". The validation is a real-file check the parser
 * cannot do to itself; when it passes, the catalog moves to `text-view`.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import { inflateSync } from 'fflate';
import type {
  FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import type { DocxBlock, DocxParagraph } from '../docx/document-xml.js';
import type { DocxModel } from '../docx/handler.js';
import { openCompoundFile } from '../cfb/cfb.js';
import type { CfbFile } from '../cfb/cfb.js';
import { paragraphsOf, parseFileHeader } from './records.js';

const PREVIEW_PARAGRAPHS = 40;

interface Read {
  readonly paragraphs: string[];
  readonly version: string;
  readonly sectionCount: number;
  readonly truncated: boolean;
}

/** Section streams in reading order: `BodyText/Section0`, `Section1`, … */
function sectionPaths(cfb: CfbFile): string[] {
  return [...cfb.streams.keys()]
    .filter((p) => /^BodyText\/Section\d+$/.test(p))
    .sort((a, b) => sectionNumber(a) - sectionNumber(b));
}

function sectionNumber(path: string): number {
  return Number(/Section(\d+)$/.exec(path)?.[1] ?? 0);
}

function read(bytes: Uint8Array, maxParagraphs: number): Read {
  const cfb = openCompoundFile(bytes);
  if (!cfb.has('FileHeader')) {
    throw new HandlerError('error.damagedOrIncomplete', 'not an HWP document (no FileHeader stream)');
  }
  const header = parseFileHeader(cfb.read('FileHeader'));
  if (!header.valid) {
    throw new HandlerError('error.damagedOrIncomplete', 'FileHeader does not carry the HWP signature');
  }
  if (header.passwordProtected || header.distributed) {
    throw new HandlerError('error.passwordProtected', 'the document is password-protected or distribution-locked');
  }

  const sections = sectionPaths(cfb);
  if (sections.length === 0) {
    throw new HandlerError('error.damagedOrIncomplete', 'the document has no BodyText sections');
  }

  const paragraphs: string[] = [];
  let truncated = false;
  for (const path of sections) {
    const raw = cfb.read(path);
    const section = header.compressed ? inflate(raw) : raw;
    for (const text of paragraphsOf(section)) {
      if (paragraphs.length >= maxParagraphs) { truncated = true; break; }
      paragraphs.push(text);
    }
    if (truncated) break;
  }

  return { paragraphs, version: header.version, sectionCount: sections.length, truncated };
}

function inflate(raw: Uint8Array): Uint8Array {
  try {
    // HWP uses raw DEFLATE, the same stream a ZIP entry carries.
    return inflateSync(raw);
  } catch (err) {
    throw new HandlerError('error.damagedOrIncomplete', `could not decompress a section: ${(err as Error).message}`);
  }
}

/** Build the shared paragraph model from extracted text. */
function toBlocks(paragraphs: readonly string[]): { blocks: DocxBlock[]; paragraphs: DocxParagraph[] } {
  const out: DocxParagraph[] = paragraphs.map((text, index) => ({
    kind: 'paragraph',
    index,
    styleId: null,
    styleName: null,
    headingLevel: null,
    alignment: null,
    listLevel: null,
    listOrdered: false,
    runs: text ? [{
      kind: 'text', text, bold: false, italic: false, underline: false,
      strike: false, vertical: null, highlight: null, color: null, href: null, revision: null,
    }] : [],
    text,
    originalText: text,
    // Read-only: HWP 5.0 is not edited in this build.
    editable: false,
    start: 0,
    end: 0,
  }));
  return { blocks: out.slice(), paragraphs: out };
}

function wordCount(paragraphs: readonly string[]): number {
  let total = 0;
  for (const p of paragraphs) {
    const t = p.trim();
    if (t) total += t.split(/\s+/).length;
  }
  return total;
}

export const hwpHandler: FormatHandler<DocxModel> = {
  id: 'hwp',
  formatIds: ['hwp'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const result = read(source.bytes, PREVIEW_PARAGRAPHS);
    const { blocks } = toBlocks(result.paragraphs);
    return {
      kind: 'pages',
      unitCount: result.truncated ? undefined : result.paragraphs.length,
      unitKey: 'viewer.paragraph',
      firstContent: { blocks: blocks.slice(0, PREVIEW_PARAGRAPHS), more: result.truncated },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<DocxModel>> {
    throwIfAborted(ctx.signal);
    const maxParagraphs = Math.max(1, Math.floor(ctx.limits.maxSpreadsheetCells / 8));
    const result = read(source.bytes, maxParagraphs);
    const { blocks, paragraphs } = toBlocks(result.paragraphs);
    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    // §19 — the honesty the `needs-validation` rating stands for, said to the
    // user rather than buried in a catalog.
    warnings.push({
      messageKey: 'status.unvalidatedFormat',
      severity: 'caution',
      detail: `HWP 5.0 text extraction (version ${result.version}). This reader is written to the specification but not yet validated against real files; formatting, tables, equations and embedded objects are not shown, and only text is read.`,
    });
    if (result.truncated) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'degraded',
        detail: 'the document exceeds the paragraph budget for this device; the remainder is not shown',
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large document (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        blocks,
        paragraphs,
        edits: new Map(),
        images: new Map(),
        hasMacros: false,
        hasTrackedChanges: false,
        hasComments: false,
        hasHeadersOrFooters: false,
        hasFootnotes: false,
        wordCount: wordCount(result.paragraphs),
      },
      // Read-only regardless of session: no HWP writer exists (§18 note).
      readOnly: true,
      editLevel: spec?.edit ?? 'needs-validation',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  // No exportBytes: HWP 5.0 is never written back (§18, catalog lossOnSave).
};

/** Plain text of the whole document, for search and copy. */
export function hwpToPlainText(model: DocxModel): string {
  return model.paragraphs.map((p) => p.text).join('\n');
}
